/* "How to get these credentials" for Amazon S3 and the S3-compatible presets: numbered steps with official links,
 * the least-privilege IAM policy (copy button), what Test connection checks and the common errors. */
import { useI18n } from "../../../i18n";
import { Button, IconButton, useToast } from "../../../ui";
import SetupGuide, { GuideSteps, LinkList } from "../components/SetupGuide";
import { AWS_LINKS, COMPAT, ERRORS, TEST_STEPS, iamPolicy } from "./s3";

export function PolicyBlock({ bucket, prefix }: { bucket: string; prefix: string }) {
  const { L } = useI18n();
  const toast = useToast();
  const text = iamPolicy(bucket, prefix);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); } catch {
      const ta = document.createElement("textarea"); ta.value = text; document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); } catch { /* ignore */ } ta.remove();
    }
    toast(L("IAM policy copied", "تم نسخ سياسة IAM"));
  };
  return (
    <div data-testid="iam-policy">
      <div className="code-block" data-testid="iam-policy-json">
        <span className="copy"><IconButton icon="copy" label={L("Copy policy", "نسخ السياسة")} onClick={copy} data-testid="copy-policy" /></span>
        {text}
      </div>
      <p className="xs muted" style={{ margin: "6px 0 0" }}>{L("Scoped to the bucket" + (prefix ? " and the folder prefix" : "") + " you typed — it updates as you type.", "مقصورة على الحاوية" + (prefix ? " وبادئة المجلد" : "") + " التي كتبتها — تتحدث أثناء الكتابة.")}</p>
    </div>
  );
}

export default function S3Guide({ bucket, prefix, open, onPreset }: { bucket: string; prefix: string; open: boolean; onPreset: (name: string) => void }) {
  const { L } = useI18n();
  const steps = [
    <>
      <b className="sm">{L("Create a bucket", "أنشئ حاوية")}</b>
      <p>{L("Amazon S3 console → Create bucket. Use a globally unique lowercase name (e.g. starthr-files-prod) and pick the region closest to you — Middle East (UAE) me-central-1 or (Bahrain) me-south-1. Leave ACLs disabled and default encryption on.",
        "لوحة Amazon S3 ← Create bucket. استخدم اسمًا فريدًا عالميًا بأحرف صغيرة (مثل starthr-files-prod) واختر أقرب منطقة لك — الشرق الأوسط (الإمارات) me-central-1 أو (البحرين) me-south-1. اترك ACLs معطلة والتشفير الافتراضي مفعلًا.")}</p>
      <LinkList links={[AWS_LINKS.createBucketConsole, AWS_LINKS.createBucket, AWS_LINKS.naming]} testPrefix="s3-guide-1" />
    </>,
    <>
      <b className="sm">{L("Keep Block Public Access on", "أبقِ حظر الوصول العام مفعلًا")}</b>
      <p>{L("Leave “Block all public access” ticked. StartHR never needs public files: downloads go through the API (or 5-minute presigned links), so receipts, IDs and payslips stay private.",
        "اترك خيار «Block all public access» محددًا. لا يحتاج StartHR ملفات عامة: التنزيل يتم عبر الواجهة البرمجية (أو روابط موقعة لمدة 5 دقائق) فتبقى الإيصالات والهويات وقسائم الرواتب خاصة.")}</p>
      <LinkList links={[AWS_LINKS.blockPublic]} testPrefix="s3-guide-2" />
    </>,
    <>
      <b className="sm">{L("Create an IAM user for StartHR", "أنشئ مستخدم IAM خاصًا بـ StartHR")}</b>
      <p>{L("IAM console → Users → Create user → name it e.g. starthr-files → do NOT give it console access (programmatic access only) → Next → skip permissions for now → Create user.",
        "لوحة IAM ← Users ← Create user ← سمّه مثلًا starthr-files ← لا تمنحه دخولًا إلى اللوحة (وصول برمجي فقط) ← Next ← تخطَّ الصلاحيات الآن ← Create user.")}</p>
      <LinkList links={[AWS_LINKS.createUserConsole, AWS_LINKS.iamUser]} testPrefix="s3-guide-3" />
    </>,
    <>
      <b className="sm">{L("Attach this least-privilege inline policy", "أرفق سياسة مضمّنة بأقل الصلاحيات")}</b>
      <p>{L("Users → (your user) → Permissions → Add permissions → Create inline policy → JSON → paste the policy below → Next → name it StartHRFiles → Create policy. It allows only s3:ListBucket on the bucket and s3:PutObject, s3:GetObject, s3:DeleteObject on its files.",
        "Users ← (المستخدم) ← Permissions ← Add permissions ← Create inline policy ← JSON ← الصق السياسة أدناه ← Next ← سمّها StartHRFiles ← Create policy. تسمح فقط بـ s3:ListBucket على الحاوية وs3:PutObject وs3:GetObject وs3:DeleteObject على ملفاتها.")}</p>
      <PolicyBlock bucket={bucket} prefix={prefix} />
    </>,
    <>
      <b className="sm">{L("Create an access key", "أنشئ مفتاح وصول")}</b>
      <p>{L("IAM → Users → (your user) → Security credentials → Access keys → Create access key → choose “Application running outside AWS” → Next → Create access key.",
        "IAM ← Users ← (المستخدم) ← Security credentials ← Access keys ← Create access key ← اختر «Application running outside AWS» ← Next ← Create access key.")}</p>
      <LinkList links={[AWS_LINKS.securityCredentials, AWS_LINKS.accessKeys]} testPrefix="s3-guide-5" />
    </>,
    <>
      <b className="sm">{L("Copy both keys now", "انسخ المفتاحين الآن")}</b>
      <p>{L("Copy the Access key ID (starts with AKIA) into “Access Key ID” and the Secret access key into “Secret Access Key”. AWS shows the secret only once — if you lose it, create a new key. StartHR stores it encrypted and never shows it again.",
        "انسخ Access key ID (يبدأ بـ AKIA) إلى «معرّف مفتاح الوصول» وSecret access key إلى «مفتاح الوصول السري». يعرض AWS السر مرة واحدة فقط — إن فقدته فأنشئ مفتاحًا جديدًا. يحفظه StartHR مشفرًا ولا يعرضه مرة أخرى.")}</p>
    </>,
    <>
      <b className="sm">{L("Find the region code", "اعرف رمز المنطقة")}</b>
      <p>{L("S3 console → your bucket → Properties → “AWS Region” (e.g. Middle East (UAE) me-central-1). Pick the same code in the Region field — it must match exactly.",
        "لوحة S3 ← الحاوية ← Properties ← «AWS Region» (مثل Middle East (UAE) me-central-1). اختر الرمز نفسه في حقل المنطقة — يجب أن يتطابق تمامًا.")}</p>
      <LinkList links={[AWS_LINKS.s3Console, AWS_LINKS.regions]} testPrefix="s3-guide-7" />
    </>,
    <>
      <b className="sm">{L("Test, then save", "اختبر ثم احفظ")}</b>
      <p>{L("With Bucket name, Region, Access Key ID and Secret Access Key filled in, press Test connection, then Save & switch.", "بعد تعبئة اسم الحاوية والمنطقة ومعرّف مفتاح الوصول ومفتاح الوصول السري اضغط اختبار الاتصال ثم حفظ وتبديل.")}</p>
    </>,
  ];
  return (
    <SetupGuide testId="s3-guide" open={open} title={L("How to get these credentials", "كيف تحصل على بيانات الاعتماد")} sub={L("Amazon S3 step by step, plus S3-compatible services", "Amazon S3 خطوة بخطوة، والخدمات المتوافقة مع S3")}>
      <GuideSteps steps={steps} testPrefix="s3-guide" />
      <section className="stack" style={{ gap: 8 }} data-testid="s3-compat">
        <h4 className="sm" style={{ margin: 0 }}>{L("S3-compatible services", "الخدمات المتوافقة مع S3")}</h4>
        <p className="xs muted" style={{ margin: 0 }}>{L("Same four fields; put the endpoint under Advanced.", "نفس الحقول الأربعة؛ ضع نقطة الاتصال ضمن الإعدادات المتقدمة.")}</p>
        {COMPAT.map((c) => (
          <details key={c.id} className="compat" data-testid={"s3-compat-" + c.id}>
            <summary>{c.name}</summary>
            <div className="stack" style={{ gap: 6 }}>
              <span className="xs">{L("Endpoint:", "نقطة الاتصال:")} <code dir="ltr" style={{ wordBreak: "break-all" }}>{c.endpoint}</code></span>
              <span className="xs">{L(c.region[0], c.region[1])}</span>
              <span className="xs">{L(c.keys[0], c.keys[1])}</span>
              <LinkList links={c.links} testPrefix={"s3-compat-" + c.id} />
              <span><Button variant="sm ghost" onClick={() => onPreset(c.preset)} data-testid={"s3-compat-use-" + c.id}>{L("Use " + c.name + " settings", "استخدام إعدادات " + c.name)}</Button></span>
            </div>
          </details>
        ))}
      </section>
      <section data-testid="s3-guide-test">
        <h4 className="sm" style={{ margin: "0 0 6px" }}>{L("What “Test connection” checks", "ما الذي يفحصه «اختبار الاتصال»")}</h4>
        <ol className="sm plain-steps">{TEST_STEPS.map(([k, t]) => <li key={k}>{L(t[0], t[1])}</li>)}</ol>
      </section>
      <section data-testid="s3-guide-errors">
        <h4 className="sm" style={{ margin: "0 0 6px" }}>{L("Common errors", "أخطاء شائعة")}</h4>
        <dl className="err-list">
          {ERRORS.map(([code, fix]) => <div key={code}><dt><code dir="ltr">{code}</code></dt><dd className="sm">{L(fix[0], fix[1])}</dd></div>)}
        </dl>
      </section>
    </SetupGuide>
  );
}
